// C:\ViYash\XPRSuvey\XPRSuvey-App\src\Components\HR\Pages\AssignAssessment\AllocateSurvey.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate,useParams  } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Search,
    ChevronLeft,
    ChevronRight,
    Inbox,
    ArrowLeft,
    Users,
    Minus,
    UserMinus,
} from "lucide-react";
import {GetCompanyHRUserList, AssignAssessmentToCompanyHr,UnassignAssessment} from "../../Services/CompanyServices";
import HRAssessmentNavTabs from "../../../../Utility/HRAssessmentNavTabs";
import { sanitizeHtml, stripHtml, DESCRIPTION_RICH_CONTENT_STYLES } from "../../../../Utility/sanitizeHtml";

const PAGE_SIZE = 10;

const initials = (name) =>
    name ? name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() : "";

const HeaderCheckbox = ({ checked, indeterminate, onChange }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return (
        <input
            ref={ref}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            className="h-4 w-4 rounded border-gray-300 text-[#7f35b2] focus:ring-[#7f35b2]"
        />
    );
};

const TableRowSkeleton = () => (
    <tr className="border-b border-gray-50 animate-pulse">
        <td className="py-3.5 pr-3"><div className="h-4 w-4 rounded bg-gray-200" /></td>
        <td className="py-3.5 pr-3">
            <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 shrink-0 rounded-full bg-gray-200" />
                <div className="h-4 w-28 rounded bg-gray-200" />
            </div>
        </td>
        <td className="py-3.5 pr-3"><div className="h-4 w-36 rounded bg-gray-200" /></td>
        <td className="py-3.5 pr-3"><div className="h-4 w-28 rounded bg-gray-200" /></td>
        <td className="py-3.5 pr-3"><div className="h-4 w-24 rounded bg-gray-200" /></td>
    </tr>
);

const AllocateSurvey = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { assessmentUID: paramAssessmentUID } = useParams();
    const { assessmentUID: stateAssessmentUID, assessmentName, description } = location.state || {};
    const assessmentUID = stateAssessmentUID || paramAssessmentUID;

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    // Selection state: selectedIds for NEW assignments, unassignIds for REMOVALS
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [unassignIds, setUnassignIds] = useState(new Set());
    const [alreadyAssignedIds, setAlreadyAssignedIds] = useState(new Set());

    const [assigning, setAssigning] = useState(false);
    const [unassigning, setUnassigning] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoading(true);
                const response = await GetCompanyHRUserList(assessmentUID);
                if (response?.data?.status === "success") {
                    const list = response?.data?.listOfHRUsers || [];
                    setUsers(list);
                    setAlreadyAssignedIds(
                        new Set(list.filter((u) => u.isAssigned).map((u) => u.hrUserUID))
                    );
                } else {
                    // CustomToast("fail", "Something went wrong please try again later");
                }
            } catch (error) {
                // CustomToast("fail", "An error occurred while fetching the Users");
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, []);

    const filteredUsers = useMemo(() => {
        return users.filter((user) => {
            const matchesSearch =
                !search ||
                user.fullName?.toLowerCase().includes(search.toLowerCase()) ||
                user.email?.toLowerCase().includes(search.toLowerCase()) ||
                user.mobileNumber?.includes(search);
            return matchesSearch;
        });
    }, [users, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const paginatedUsers = filteredUsers.slice(startIndex, startIndex + PAGE_SIZE);

    // Header checkbox only applies to UNASSIGNED users in the current filtered set
    const selectableFilteredUsers = useMemo(
        () => filteredUsers.filter((u) => !alreadyAssignedIds.has(u.hrUserUID)),
        [filteredUsers, alreadyAssignedIds]
    );

    const allFilteredSelected =
        selectableFilteredUsers.length > 0 &&
        selectableFilteredUsers.every((u) => selectedIds.has(u.hrUserUID));

    const someFilteredSelected =
        !allFilteredSelected && selectableFilteredUsers.some((u) => selectedIds.has(u.hrUserUID));

    // Toggle directs to assignment or unassignment set depending on current status
    const toggleUser = (id) => {
        if (alreadyAssignedIds.has(id)) {
            setUnassignIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        } else {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        }
    };

    const toggleSelectAllFiltered = () => {
        setSelectedIds((previous) => {
            const next = new Set(previous);
            if (allFilteredSelected) {
                selectableFilteredUsers.forEach((u) => next.delete(u.hrUserUID));
            } else {
                selectableFilteredUsers.forEach((u) => next.add(u.hrUserUID));
            }
            return next;
        });
    };

    const handleAssignSelected = async () => {
        if (selectedIds.size === 0 || !assessmentUID) return;
        const idsToAssign = Array.from(selectedIds);
        try {
            setAssigning(true);
            const response = await AssignAssessmentToCompanyHr({
                AssessmentUID: assessmentUID,
                HRUserUIDs: idsToAssign,
            });
            if (response?.data?.status?.toLowerCase() === "success") {
                CustomToast("success", response.data.message);
                setAlreadyAssignedIds((prev) => new Set([...prev, ...idsToAssign]));
                setSelectedIds(new Set());
            } else {
                CustomToast("fail", response.data.message);
            }
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "An error occurred while assigning the assessment.");
        } finally {
            setAssigning(false);
        }
    };

    const handleUnassignSelected = async () => {
        if (unassignIds.size === 0 || !assessmentUID) return;
        const idsToUnassign = Array.from(unassignIds);
        try {
            setUnassigning(true);
            const response = await UnassignAssessment({
                AssessmentUID: assessmentUID,
                HRUserUIDs: idsToUnassign,
            });
            if (response?.data?.status?.toLowerCase() === "success") {
                CustomToast("success", response.data.message);
                setAlreadyAssignedIds((prev) => {
                    const next = new Set(prev);
                    idsToUnassign.forEach((id) => next.delete(id));
                    return next;
                });
                setUnassignIds(new Set());
            } else {
                CustomToast("fail", response.data.message);
            }
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "An error occurred while unassigning users.");
        } finally {
            setUnassigning(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#f7f8fa]">
            <div className="mx-auto max-w-[1440px] py-2">
                <button
                    onClick={() => navigate(-1)}
                    className="mb-3 flex items-center gap-1.5 rounded-lg bg-[#7f35b2] px-3.5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#6d2b98]"
                >
                    <ArrowLeft size={16} />
                    Back
                </button>

                <HRAssessmentNavTabs
                    active="assign"
                    assessmentUID={assessmentUID}
                    assessmentName={assessmentName}
                    description={description}
                />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mx-auto max-w-[1440px] pb-6"
            >
                {assessmentName && (
                    <div className="mb-4 rounded-2xl bg-gradient-to-br from-[#7f35b2]/10 to-[#7f35b2]/5 p-4">
                        <h2 className="text-base font-semibold text-gray-900">{assessmentName}</h2>
                        {description && (
                            <>
                                <style>{DESCRIPTION_RICH_CONTENT_STYLES}</style>
                                <div
                                    className="description-rich-content mt-1 text-xs text-gray-600 line-clamp-2"
                                    title={stripHtml(description)}
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
                                />
                            </>
                        )}
                    </div>
                )}

                <div className="mb-4">
                    <h1 className="text-2xl font-bold text-gray-900">Manage Users</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Select the users you want to assign or unassign from this assessment.
                    </p>
                </div>

                <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-md">
                    {/* Filters */}
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                        <div className="relative min-w-[220px] flex-1">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, email or mobile..."
                                className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    <th className="w-10 py-3 pl-1 pr-3">
                                        <HeaderCheckbox
                                            checked={allFilteredSelected}
                                            indeterminate={someFilteredSelected}
                                            onChange={toggleSelectAllFiltered}
                                        />
                                    </th>
                                    <th className="py-3 pr-3">User</th>
                                    <th className="py-3 pr-3">Email</th>
                                    <th className="py-3 pr-3">Mobile Number</th>
                                    <th className="py-3 pr-3">Created On</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, idx) => <TableRowSkeleton key={idx} />)
                                ) : paginatedUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="py-12 text-center text-gray-400">
                                            <div className="flex flex-col items-center justify-center gap-2">
                                                <Inbox size={32} className="text-gray-300" />
                                                <p className="text-sm font-medium">No users found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedUsers.map((user) => {
                                        const isAlreadyAssigned = alreadyAssignedIds.has(user.hrUserUID);
                                        const isToAssign = selectedIds.has(user.hrUserUID);
                                        const isToUnassign = unassignIds.has(user.hrUserUID);

                                        return (
                                            <tr
                                                key={user.hrUserUID}
                                                onClick={() => toggleUser(user.hrUserUID)}
                                                className={`cursor-pointer border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors ${isToAssign ? "bg-[#c2a8f033]" : isToUnassign ? "bg-red-50" : ""
                                                    }`}
                                            >
                                                <td className="py-3  pl-1  pr-3" onClick={(e) => e.stopPropagation()}>
                                                    {isAlreadyAssigned ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleUser(user.hrUserUID)}
                                                            className={`flex h-4 w-4 items-center justify-center rounded border focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors ${isToUnassign
                                                                ? "border-gray-300 bg-white hover:border-gray-400 focus:ring-gray-300"
                                                                : "border-[#7f35b2] bg-[#7f35b2] text-white hover:bg-[#3bb290] focus:ring-[#7f35b2]"
                                                                }`}
                                                        >
                                                            {!isToUnassign && <Minus size={14} strokeWidth={3} />}
                                                        </button>
                                                    ) : (
                                                        <input
                                                            type="checkbox"
                                                            checked={isToAssign}
                                                            onChange={() => toggleUser(user.hrUserUID)}
                                                            className="h-4 w-4 rounded border-gray-300 text-[#7f35b2] focus:ring-[#7f35b2]"
                                                        />
                                                    )}
                                                </td>
                                                <td className="py-3 pr-3">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#c2a8f033] text-xs font-semibold text-[#7f35b2]">
                                                            {initials(user.fullName)}
                                                        </div>
                                                        <span className="font-medium text-gray-900">{user.fullName}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 pr-3 text-gray-500">{user.email}</td>
                                                <td className="py-3 pr-3 text-gray-500">{user.mobileNumber}</td>
                                                <td className="py-3 pr-3 text-gray-500">
                                                    {user.createdOn ? new Date(user.createdOn).toLocaleDateString() : "-"}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer: selection summary / pagination + assign/unassign actions */}
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                        <p className="text-gray-500">
                            {selectedIds.size > 0 || unassignIds.size > 0 ? (
                                <>
                                    {selectedIds.size > 0 && <span className="text-[#7f35b2] font-medium">{selectedIds.size} to assign </span>}
                                    {selectedIds.size > 0 && unassignIds.size > 0 && <span> | </span>}
                                    {unassignIds.size > 0 && <span className="text-red-500 font-medium">{unassignIds.size} to unassign</span>}
                                </>
                            ) : (
                                `Showing ${paginatedUsers.length > 0 ? startIndex + 1 : 0} to ${startIndex + paginatedUsers.length} of ${filteredUsers.length} users`
                            )}
                        </p>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <ChevronLeft size={15} />
                                </button>
                                <span className="px-2 text-sm text-gray-600">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <ChevronRight size={15} />
                                </button>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={handleUnassignSelected}
                                    disabled={unassignIds.size === 0 || unassigning}
                                    className="flex items-center gap-2 rounded-lg border border-red-500 bg-white px-4 py-2.5 text-sm font-medium text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                                >
                                    <UserMinus size={16} />
                                    {unassigning ? "Unassigning..." : `Unassign${unassignIds.size > 0 ? ` (${unassignIds.size})` : ""}`}
                                </button>

                                <button
                                    onClick={handleAssignSelected}
                                    disabled={selectedIds.size === 0 || assigning}
                                    className="flex items-center gap-2 rounded-lg bg-[#7f35b2] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#3bb290] disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                                >
                                    <Users size={16} />
                                    {assigning ? "Assigning..." : `Assign${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default AllocateSurvey;